import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  toggleTheme,
  setNotificationPanelOpen,
  setProfileDropdownOpen,
  setSearchOpen,
} from "../store/slices/uiSlice";
import { useNotifications } from "../hooks/useNotifications";
import { cn } from "@/lib/utils";
import {
  Search,
  Bell,
  Wallet,
  ArrowRightLeft,
  ChevronDown,
  User,
  Sun,
  Moon,
  LogOut,
  Shield,
  Building2,
  ArrowLeftRight,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logOut } from "@/store/actions/authActions";
import ScrollToTop from "./ScrollToTop";
import SmoothScroll from "./SmoothScroll";
import { Toaster } from "sonner";
import { MobileBottomNav } from "./mobile";
import { UserRole } from "@/interfaces/profile";
import { Logo } from "./Logo";

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { theme, notificationPanelOpen, profileDropdownOpen } = useAppSelector(
    (state) => state.ui,
  );
  const { user } = useAppSelector((state) => state.auth);
  const { notifications, unreadCount } = useNotifications();

  const isLandingPage = location.pathname === "/";

  const getDashboardRoute = () => {
    switch (user?.role) {
      case UserRole.ADMIN:
      case UserRole.SUPER:
        return "/admin";

      case UserRole.VENDOR:
        return "/vendor";

      default:
        return "/dashboard";
    }
  };

  // const showBottomNav =
  //   user &&
  //   (location.pathname.startsWith("/dashboard") ||
  //     location.pathname.startsWith("/admin") ||
  //     location.pathname.startsWith("/vendor"));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (searchRef.current && !searchRef.current.contains(target)) {
        dispatch(setSearchOpen(false));
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(target)
      ) {
        dispatch(setNotificationPanelOpen(false));
      }

      if (profileRef.current && !profileRef.current.contains(target)) {
        dispatch(setProfileDropdownOpen(false));
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dispatch]);

  const navLinks = [
    // { name: "Markets", href: "/markets", icon: Globe },
    { name: "P2P", href: "/p2p", icon: ArrowRightLeft },
    { name: "My Trades", href: "/dashboard/trades", icon: ArrowLeftRight },
    { name: "Convert", href: "/convert", icon: ArrowLeftRight },
  ];

  const searchResults = [
    {
      type: "asset",
      name: "Bitcoin",
      symbol: "BTC",
      href: "/spot?pair=BTC/USDT",
    },
    {
      type: "asset",
      name: "Ethereum",
      symbol: "ETH",
      href: "/spot?pair=ETH/USDT",
    },
    {
      type: "asset",
      name: "Nigeria Token",
      symbol: "NGNT",
      href: "/spot?pair=NGNT/USDT",
    },
    { type: "page", name: "P2P Trading", href: "/p2p" },
    { type: "page", name: "Wallet", href: "/wallet" },
  ].filter(
    (item) =>
      searchQuery &&
      (item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.symbol?.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const handleLogout = async () => {
    await dispatch(logOut());
    dispatch(setProfileDropdownOpen(false));
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="bottom-right" richColors />
      <ScrollToTop />
      <SmoothScroll />
      {/* Header */}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-300",
          isScrolled || !isLandingPage
            ? "bg-card border-b border-border"
            : "bg-transparent",
        )}
      >
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-8">
            <Logo size="xl" className="transition-transform duration-300" />

            {/* <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center">
                <span className="text-black font-bold">B</span>
              </div>
              <span className="text-xl font-bold text-foreground">BUYFIAT</span>
            </Link> */}

            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    location.pathname === link.href
                      ? "text-gold bg-gold/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent",
                  )}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Center: Search */}
          <div
            className="hidden md:flex flex-1 max-w-md mx-8 relative"
            ref={searchRef}
          >
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search markets, assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold transition-colors"
              />
            </div>

            {/* Search Results Dropdown */}
            {searchQuery && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50">
                {searchResults.map((result, i) => (
                  <Link
                    key={i}
                    to={result.href}
                    onClick={() => setSearchQuery("")}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors"
                  >
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                      {result.type}
                    </span>
                    <span className="text-foreground">{result.name}</span>
                    {result.symbol && (
                      <span className="text-gold font-mono">
                        {result.symbol}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={() => dispatch(toggleTheme())}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {user && (
                    <div className="relative" ref={profileRef}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(setProfileDropdownOpen(false));
                          navigate(getDashboardRoute());
                        }}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors"
                      >
                        <User className="w-5 h-5" />
                        <ChevronDown className="w-4 h-4" />
                      </button>

                      {profileDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-xl z-50">
                          <button
                            onClick={() => {
                              dispatch(setProfileDropdownOpen(false));
                              navigate(getDashboardRoute());
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent"
                          >
                            <User className="w-4 h-4" />
                            Profile
                          </button>

                          <button
                            onClick={() => {
                              // dispatch(setProfileDropdownOpen(false));
                              handleLogout();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent text-red-500"
                          >
                            <LogOut className="w-4 h-4" />
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="hidden lg:block text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={cn(isLandingPage ? "" : "pt-16")}>
        <Outlet />
      </main>

      <div className="lg:hidden">
        <MobileBottomNav userType="index" />
      </div>
    </div>
  );
};

export default Layout;
